import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "../LanguageContext";
import { Chapter, Reveal } from "./Chapter";

const Disciplines = () => {
  const { t } = useLang();
  const d = t.disciplines;
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (window.__lenis) active ? window.__lenis.stop() : window.__lenis.start();
  }, [active]);

  return (
    <Chapter
      id="competenze"
      num={d.num}
      kicker={d.kicker}
      title={d.title}
      className="bg-sand"
      aside={
        <img
          src="/images/discipline-header.jpg"
          alt="Corpo, Mente, Emozioni, Energia — Metodo SynergyA"
          data-testid="disciplines-header-image"
          className="w-full rounded-2xl border border-ink/10 shadow-lg shadow-forest/10"
        />
      }
    >
      <Reveal>
        <p className="mb-12 max-w-xl text-base font-light text-moss">{d.sub}</p>
      </Reveal>
      <div className="grid grid-cols-1 gap-5 min-[480px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {d.items.filter((item) => item.id !== "synergya").map((item, i) => (
          <Reveal key={item.id} delay={(i % 4) * 0.06}>
            <motion.button
              data-testid={`discipline-card-${item.id}`}
              onClick={() => setActive(item)}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="group flex aspect-square w-full flex-col overflow-hidden rounded-2xl border border-ink/10 bg-linen text-left shadow-sm transition-shadow duration-500 hover:shadow-xl hover:shadow-forest/10"
            >
              <div className="flex min-h-0 flex-1 items-center justify-center bg-sand p-5">
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-[1.04]"
                />
              </div>
              <div className="flex shrink-0 flex-col gap-1.5 p-5">
                <span className="font-mono text-[10px] tracking-[0.25em] text-terra">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="line-clamp-2 font-serif text-lg font-normal leading-snug tracking-tight text-forest">
                  {item.title}
                </h3>
                <p className="line-clamp-2 text-xs font-light leading-relaxed text-moss">{item.teaser}</p>
              </div>
            </motion.button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            key="discipline-modal"
            data-testid="discipline-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[95] flex items-end justify-center bg-forest/70 p-0 backdrop-blur-md sm:items-center sm:p-8"
          >
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[92vh] w-full max-w-3xl overflow-y-auto bg-linen sm:max-h-[85vh]"
            >
              <div className="relative h-52 sm:h-64">
                <img src={active.image} alt={active.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-forest/35" />
                <button
                  data-testid="discipline-modal-close"
                  onClick={() => setActive(null)}
                  aria-label={d.close}
                  className="absolute right-4 top-4 rounded-full bg-linen/90 p-2.5 text-forest transition-colors duration-300 hover:bg-terra hover:text-linen"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <h3 className="absolute bottom-5 left-6 right-6 font-serif text-3xl font-light tracking-tight text-linen sm:text-4xl">
                  {active.title}
                </h3>
              </div>
              <div className="space-y-10 p-6 sm:p-10">
                {active.sections.map((s, i) => (
                  <div key={i}>
                    <h4 className="flex items-baseline gap-3 font-serif text-xl font-normal text-terra sm:text-2xl">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-moss">{String(i + 1).padStart(2, "0")}</span>
                      {s.h}
                    </h4>
                    {s.p && s.p.map((p, j) => (
                      <p key={j} className="mt-4 text-sm font-light leading-relaxed text-ink/85 sm:text-base">{p}</p>
                    ))}
                    {s.list && (
                      <ul className="mt-5 grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
                        {s.list.map((li, j) => (
                          <li key={j} className="flex gap-2.5 text-sm font-light leading-relaxed text-moss">
                            <span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-terra" />
                            {li}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Chapter>
  );
};

export default Disciplines;
